<template>
  <div>
    <label for="history" :disabled="disabled" class="form-label ms-1">
      {{ $t('resultDetails.tagDetails.toggleButton.history') }}
    </label>
    <div id="history" class="d-flex align-items-center position-relative">
      <div
        class="btn-group puls-history-selector"
        role="group"
        aria-label="Time period"
      >
        <!-- :title="titleForTooltip(t.value)" -->
        <button
          v-for="t in timePeriodOptions"
          :key="t.value"
          :disabled="disabled"
          class="puls-history-selector__btn btn btn-sm btn-outline-secondary"
          :class="activeClass(t.value)"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          :data-bs-original-title="titleForTooltip(t.value)"
          @pointerdown.capture.stop.prevent="
            t.value === 'custom' ? toggleCalendar($event) : null
          "
          @click="t.value !== 'custom' ? handleSelectTimePeriod(t.value) : null"
        >
          <span v-if="t.value === 'custom'">
            <i class="bi bi-calendar-event fs-5"></i>
          </span>
          <span v-else>
            {{ t.label }}
          </span>
        </button>
      </div>
      <div class="puls-date-picker">
        <Datepicker
          ref="datepicker"
          v-model="selectedDate"
          :max-date="currentSurveyEndDate"
          :autoApply="true"
          :open="isCalendarOpen"
          :enable-time-picker="false"
          :selectText="$t('general.ok')"
          :cancelText="$t('general.cancel')"
          :locale="locale"
          :yearRange="yearRange"
          :startDate="
            currentSurveyEndDate ? new Date(currentSurveyEndDate) : new Date()
          "
          @open="onDatepickedOpen"
          @closed="onDatepickerClose"
        >
        </Datepicker>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import {
  timeObjectFromSelectedTimePeriod,
  getTimePeriodSinceSurveyEndDate
} from '@/helpers/time-window-helper'

import { timePeriodsWithLabels } from '@/constants/time-types'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

export default {
  name: 'HistorySelector',
  components: {
    Datepicker
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    selectedTimePeriod: {
      type: String,
      default: ''
    },
    surveyDetails: {
      type: Object,
      default: () => {}
    }
  },
  emits: ['timePeriodSelected'],
  data() {
    return {
      // startDate: '',
      selectedDate: null,
      isCalendarOpen: false,
      timePeriodOptions: timePeriodsWithLabels(this.$t)
    }
  },
  computed: {
    currentSurveyEndDate() {
      return this.surveyDetails.details.end_date
    },
    locale() {
      return localStorage.selectedLocale || 'en'
    },
    yearRange() {
      const currentSurveyEndDateYear = new Date(
        this.currentSurveyEndDate
      ).getFullYear()
      return [2010, currentSurveyEndDateYear]
    }
  },
  watch: {
    selectedDate() {
      if (this.selectedDate) {
        const daysBetweenSurveyEndDateAndSelectedDate = Math.floor(
          (new Date(this.currentSurveyEndDate) - this.selectedDate) /
            (1000 * 60 * 60 * 24)
        )
        this.$emit(
          'timePeriodSelected',
          `${daysBetweenSurveyEndDateAndSelectedDate}d`
        )
      }
    }
  },
  methods: {
    activeClass(value) {
      if (value !== 'custom' && value === this.selectedTimePeriod) {
        return 'active'
      }
      if (value === 'custom' && this.selectedDate) {
        return 'active'
      }
    },
    openDatepicker() {
      this.$refs.datepicker.openMenu()
    },
    closeDatepicker(event) {
      this.$refs.datepicker.closeMenu()
    },
    titleForTooltip(value) {
      const timePeriod = timeObjectFromSelectedTimePeriod({
        selectedTimePeriod: value === 'custom' ? this.selectedTimePeriod : value
      })
      const timePeriodAgo = getTimePeriodSinceSurveyEndDate({
        surveyDetails: this.surveyDetails,
        timePeriod
      })
      const date = DateTime.fromISO(timePeriodAgo).toLocaleString(
        DateTime.DATE_FULL
      )
      if (value !== 'custom') {
        if (value === this.selectedTimePeriod) {
          return this.$t('history.selectorDescriptions.selectedDate', {
            date
          })
        } else {
          return this.$t('history.selectorDescriptions.selectDate', {
            date
          })
        }
      } else {
        return this.selectedDate !== null
          ? this.$t('history.selectorDescriptions.selectedDate', {
              date
            })
          : this.$t('history.selectorDescriptions.noCustomDateSelected')
      }
    },
    toggleCalendar(event) {
      // Stop the event from propagating so it doesn't trigger additional handlers
      event.stopPropagation()
      if (this.isCalendarOpen) {
        this.closeDatepicker()
      } else {
        this.openDatepicker()
      }
    },
    handleSelectTimePeriod(value) {
      this.selectedDate = null
      this.setSelectedTimePeriod(value)
    },
    setSelectedTimePeriod(value) {
      this.$emit('timePeriodSelected', value)
    },
    onDatepickedOpen() {
      this.isCalendarOpen = true
    },
    onDatepickerClose() {
      this.isCalendarOpen = false
    }
  }
}
</script>

<style lang="scss">
.puls-history-selector {
  min-height: 40px;
  // color: var(--kt-input-color);
  background-color: var(--kt-input-bg);
  // border: 1px solid var(--kt-input-border-color);
  &__btn {
    max-width: 180px;
    border: 1px solid var(--kt-input-border-color) !important;
    &:hover {
      background-color: var(--kt-secondary);
    }
    &.active {
      background-color: var(--kt-primary);
    }
  }
}

.puls-date-picker {
  position: absolute;
  right: 30px;
  top: 30px;

  .dp__month_year_wrap {
    gap: 0.5rem !important;
  }

  button {
    padding: inherit !important;
  }

  .dp__outer_menu_wrap {
    margin-top: 10px;
  }
  .dp__inner_nav {
    border-radius: var(--bs-border-radius-sm);
  }
  .calendar {
    &::before {
      display: none;
    }
  }
  .dp__input_wrap,
  .dp__selection_preview {
    display: none;
  }
  .dp__overlay {
    border-radius: var(--bs-border-radius);
  }
}
.dp__theme_light {
  // backdrop-filter: blur(1px);
  --dp-text-color: var(--kt-dark);
  --dp-hover-color: var(--kt-gray-300);
  --dp-hover-text-color: var(--kt-gray-900);
  --dp-background-color: var(--kt-gray-200);
  --dp-primary-color: var(--kt-primary);
  --dp-font-family: inherit !important;
  --dp-border-radius: var(--bs-border-radius);
  --dp-action-buttons-padding: calc(0.55rem + 1px) calc(1.25rem + 1px);
  --dp-action-button-height: none !important;
  --dp-animation-duration: 0s; /*Transition duration*/
  --dp-overlay-col-padding: 0px;
}
</style>

<style scoped>
.calendar-icon {
  cursor: pointer;
  font-size: 1.5em;
}
</style>
